/**
 * Module dependencies.
 */

import { ComponentProps } from 'react';
import { Control, FieldValues, Path, RegisterOptions, UseControllerProps, useController } from 'react-hook-form';
import { assignRefs } from 'src/core/utils/refs';

import { Select, SelectProps } from './index';

/**
 * Export `SelectFieldProps` interface.
 */

export type SelectFieldProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  defaultValue?: any;
  hideErrors?: boolean;
  name: string;
  ref?: ComponentProps<typeof Select>['ref'];
  rules?: RegisterOptions;
} & Omit<SelectProps, 'hasError' | 'hasValue' | 'id' | 'onChange'>;

/**
 * Export `SelectField` component.
 */

export function SelectField<T extends FieldValues>(props: SelectFieldProps<T>) {
  const { control, defaultValue, hideErrors, menuPlacement = 'auto', name, ref: forwardedRef, rules, ...rest } = props;

  const {
    field: { ref, ...inputProps },
    fieldState: { error }
  } = useController({
    control,
    defaultValue,
    name: name as Path<T>,
    rules: rules as UseControllerProps<T>['rules']
  });

  return (
    <Select
      {...rest}
      {...inputProps}
      error={hideErrors ? undefined : error?.message}
      id={name}
      menuPlacement={menuPlacement}
      ref={assignRefs(forwardedRef, ref)}
    />
  );
}
